import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Plus,
  Trash2,
  Bot,
  History,
  Play,
  Pause,
  GitBranch,
  Loader2,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/hooks/use-toast";
import {
  listAgents,
  deleteAgent,
  listAgentVersions,
  publishAgentVersion,
  unpublishAgentVersion,
  listAgentTypes,
  type Agent,
  type AgentVersion,
  type AgentType,
} from "@/services/agentsService";
import { AgentForm } from "./AgentForm";
import { usePermissions } from "@/hooks/usePermissions";
import { PermissionGuard } from "@/components/auth/PermissionGuard";

interface AgentManagementProps {
  onBack: () => void;
}

type ViewMode = "list" | "form" | "versions";

export const AgentManagement = ({ onBack }: AgentManagementProps) => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [agentTypes, setAgentTypes] = useState<AgentType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [isVersionDialogOpen, setIsVersionDialogOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<AgentVersion | null>(
    null
  );
  const { toast } = useToast();
  const { canCreate, canUpdate, canDelete } = usePermissions();

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setIsLoading(true);
    try {
      const [agentsResponse, typesResponse] = await Promise.all([
        listAgents(),
        listAgentTypes(),
      ]);

      if (agentsResponse.success && agentsResponse.data) {
        setAgents(agentsResponse.data.agents);
      } else {
        toast({
          title: "Erro",
          description: agentsResponse.error?.message || "Erro ao carregar agentes",
          variant: "destructive",
        });
      }

      if (typesResponse.success && typesResponse.data) {
        setAgentTypes(typesResponse.data);
      } else {
        toast({
          title: "Erro",
          description: typesResponse.error?.message || "Erro ao carregar tipos de agentes",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao conectar com o servidor",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadAgents = async () => {
    try {
      const response = await listAgents();

      if (response.success && response.data) {
        setAgents(response.data.agents);
      } else {
        toast({
          title: "Erro",
          description: response.error?.message || "Erro ao carregar agentes",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao conectar com o servidor",
        variant: "destructive",
      });
    }
  };

  const loadAgentVersions = async (agentId: string) => {
    try {
      const response = await listAgentVersions(agentId);

      if (response.success && response.data) {
        return response.data;
      }
      return [];
    } catch (error) {
      return [];
    }
  };

  const handleSaveAgent = async () => {
    await loadAgents();
    setViewMode("list");
    setEditingAgent(null);
  };

  const handleNewAgent = () => {
    setEditingAgent(null);
    setViewMode("form");
  };

  const handleEditAgent = async (agent: Agent) => {
    setEditingAgent(agent);
    setViewMode("form");
  };

  const handleEditVersion = async (agent: Agent, version: AgentVersion) => {
    setEditingAgent(agent);
    setIsVersionDialogOpen(false);
    setViewMode("form");
  };

  const handleViewVersions = async (agent: Agent) => {
    setIsLoading(true);
    try {
      const versions = await loadAgentVersions(agent.id);
      setSelectedAgent({ ...agent, versions });
      setIsVersionDialogOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePublishVersion = async (versionId: string) => {
    if (!selectedAgent) return;

    setIsLoading(true);
    try {
      const response = await publishAgentVersion(versionId);

      if (response.success) {
        toast({
          title: "Sucesso",
          description: "Versão publicada e ativada com sucesso.",
        });

        const versions = await loadAgentVersions(selectedAgent.id);
        setSelectedAgent({ ...selectedAgent, versions });
        await loadAgents();
      } else {
        toast({
          title: "Erro",
          description: response.error?.message || "Erro ao publicar versão",
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnpublishVersion = async (versionId: string) => {
    if (!selectedAgent) return;

    if (
      !confirm("Despublicar criará uma nova versão major (X.0.0). Confirma?")
    ) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await unpublishAgentVersion(versionId);

      if (response.success) {
        toast({
          title: "Sucesso",
          description:
            "Versão despublicada e nova versão major criada com sucesso.",
        });

        const versions = await loadAgentVersions(selectedAgent.id);
        setSelectedAgent({ ...selectedAgent, versions });
        await loadAgents();
      } else {
        toast({
          title: "Erro",
          description: response.error?.message || "Erro ao despublicar versão",
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAgent = async (agentId: string) => {
    if (!confirm("Tem certeza que deseja excluir este agente?")) return;

    setIsLoading(true);
    try {
      const response = await deleteAgent(agentId);

      if (response.success) {
        toast({
          title: "Sucesso",
          description: "Agente removido com sucesso.",
        });
        await loadAgents();
      } else {
        toast({
          title: "Erro",
          description: response.error?.message || "Erro ao remover agente",
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (viewMode === "form") {
    return (
      <AgentForm
        onBack={() => setViewMode("list")}
        onSave={handleSaveAgent}
        editingAgent={editingAgent}
        agentTypes={agentTypes}
      />
    );
  }

  return (
    <PermissionGuard
      permission="res_agents.read"
      fallback={
        <div className="min-h-screen flex items-center justify-center p-8">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle>Acesso negado</CardTitle>
              <CardDescription>Você não tem permissão para visualizar agentes.</CardDescription>
            </CardHeader>
          </Card>
        </div>
      }
    >
    <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      <PageHeader
        icon={Bot}
        title="Gerenciamento de Agentes"
        description="Configure agentes e seus prompts com versionamento"
        onBack={onBack}
        actions={
          canCreate("agents") && (
            <Button
              onClick={handleNewAgent}
              className="bg-gradient-primary hover:opacity-90"
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Agente
            </Button>
          )
        }
      />

      <main className="container mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle>Agentes Configurados</CardTitle>
            <CardDescription>
              Gerencie os agentes de IA, suas versões e configure seus prompts
              de processamento
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading && agents.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Serviço</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Versão Ativa</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Data Criação</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {agents.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="text-center py-8 text-muted-foreground"
                      >
                        <Bot className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>Nenhum agente configurado</p>
                        <p className="text-sm">
                          Clique em "Novo Agente" para começar
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    agents.map((agent) => {
                      return (
                        <TableRow key={agent.id}>
                          <TableCell className="font-medium">
                            {agent.name}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{agent.type_name || "N/A"}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{agent.service}</Badge>
                          </TableCell>
                          <TableCell className="max-w-xs truncate">
                            {agent.description}
                          </TableCell>
                          <TableCell>
                            {agent.active_version ? (
                              <div className="flex items-center space-x-2">
                                <Badge variant="outline">
                                  {agent.active_version}
                                </Badge>
                                <Badge variant="default" className="text-xs">
                                  Ativa
                                </Badge>
                              </div>
                            ) : (
                              <Badge variant="secondary">Nenhuma</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                agent.is_active ? "default" : "secondary"
                              }
                            >
                              {agent.is_active ? "Ativo" : "Inativo"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {new Date(agent.created_at).toLocaleDateString(
                              "pt-BR"
                            )}
                          </TableCell>
                          <TableCell className="text-right space-x-2">
                            {canUpdate("agents") && (
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => handleViewVersions(agent)}
                                className="h-8 w-8"
                                title="Ver versões"
                                disabled={isLoading}
                              >
                                <History className="h-3 w-3" />
                              </Button>
                            )}
                            {canUpdate("agents") && (
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => handleEditAgent(agent)}
                                className="h-8 w-8"
                                title="Editar agente"
                                disabled={isLoading}
                              >
                                <GitBranch className="h-3 w-3" />
                              </Button>
                            )}
                            {canDelete("agents") && (
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => handleDeleteAgent(agent.id)}
                                className="h-8 w-8 hover:bg-destructive/10 hover:text-destructive"
                                title="Excluir agente"
                                disabled={isLoading}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Version Management Dialog */}
      <Dialog open={isVersionDialogOpen} onOpenChange={setIsVersionDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <DialogTitle>Versões - {selectedAgent?.name}</DialogTitle>
              {selectedAgent?.type_name && (
                <Badge variant="outline">{selectedAgent.type_name}</Badge>
              )}
            </div>
            <DialogDescription>
              Gerencie as versões do agente, publique e ative diferentes versões
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {selectedAgent?.versions && selectedAgent.versions.length > 0 ? (
              selectedAgent.versions.map((version) => (
                <Card
                  key={version.id}
                  className={`p-4 ${
                    version.is_active ? "ring-2 ring-primary" : ""
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <Badge variant="outline" className="font-mono">
                          {version.version}
                        </Badge>
                        <Badge
                          variant={
                            version.is_published ? "default" : "secondary"
                          }
                        >
                          {version.is_published ? "Publicada" : "Rascunho"}
                        </Badge>
                        {version.is_active && (
                          <Badge variant="default" className="bg-green-500">
                            Ativa
                          </Badge>
                        )}
                      </div>
                      <h4 className="font-medium">{version.description}</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        Criada em:{" "}
                        {new Date(version.created_at).toLocaleDateString(
                          "pt-BR"
                        )}
                        {version.published_at &&
                          ` • Publicada em: ${new Date(
                            version.published_at
                          ).toLocaleDateString("pt-BR")}`}
                      </p>
                      <div className="mt-3">
                        <Badge variant="outline" className="text-xs">
                          {version.steps.length}{" "}
                          {version.steps.length === 1 ? "prompt" : "prompts"}
                        </Badge>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {/* Botões de ação da versão (requer update) */}
                      {canUpdate("agents") && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              handleEditVersion(selectedAgent, version)
                            }
                            className="bg-blue-50 hover:bg-blue-100"
                            disabled={isLoading}
                            title={
                              version.is_published
                                ? "Editar (cria nova versão ou atualiza rascunho)"
                                : "Editar versão rascunho"
                            }
                          >
                            <GitBranch className="h-3 w-3 mr-1" />
                            Editar
                          </Button>

                          {/* Botão Publicar - apenas para rascunhos */}
                          {!version.is_published && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePublishVersion(version.id)}
                              className="bg-green-50 hover:bg-green-100"
                              disabled={isLoading}
                            >
                              <Play className="h-3 w-3 mr-1" />
                              Publicar
                            </Button>
                          )}

                          {/* Botão Despublicar - apenas para versões publicadas e ativas */}
                          {version.is_published && version.is_active && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUnpublishVersion(version.id)}
                              className="bg-orange-50 hover:bg-orange-100"
                              disabled={isLoading}
                            >
                              <Pause className="h-3 w-3 mr-1" />
                              Despublicar
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Show prompts for selected version */}
                  {selectedVersion?.id === version.id && (
                    <div className="mt-4 pt-4 border-t">
                      <h5 className="font-medium mb-3">
                        Prompts desta versão:
                      </h5>
                      <div className="space-y-3">
                        {version.steps.map((prompt, index) => (
                          <div
                            key={prompt.id || index}
                            className="bg-muted/50 p-3 rounded-lg"
                          >
                            <div className="flex items-center space-x-2 mb-2">
                              <Badge variant="outline" className="text-xs">
                                Etapa {index + 1}
                              </Badge>
                              <span className="text-sm font-medium">
                                {prompt.name}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {prompt.prompt}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setSelectedVersion(
                        selectedVersion?.id === version.id ? null : version
                      )
                    }
                    className="mt-3 w-full"
                    disabled={isLoading}
                  >
                    {selectedVersion?.id === version.id
                      ? "Ocultar Prompts"
                      : "Ver Prompts"}
                  </Button>
                </Card>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Nenhuma versão criada ainda</p>
                <p className="text-sm">Crie uma nova versão para começar</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsVersionDialogOpen(false)}
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </PermissionGuard>
  );
};
